import { useState } from 'react';
import {
  type DelegateFederalApproverResolveResponse,
  useContractRoleAssignmentsQuery,
  useDeleteContractRoleAssignment,
  useResolveDelegateFederalApprover,
  useUpdateContract,
  useUpsertDelegateFederalApprover,
} from '@access-cms/api-client';

import { ContractFormActions } from './components/ContractFormActions';
import { ConfirmActionModal } from './components/ConfirmActionModal';
import { useContractData } from './context';

import Dropdown from '@/components/Dropdown';
import TextField from '@/components/TextField';
import { ValidationAnnouncement } from '@/components/ValidationAnnouncement';
import { Drawer } from '@/components/Drawer';
import { HelpDrawerLink } from '@/components/HelpDrawerLink';
import { RequiredFieldsLegend } from '@/components/RequiredFieldsLegend';
import { useAlerts } from '@/hooks';
import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';
import { useValidationAnnouncement } from '@/hooks/useValidationAnnouncement';

const DROPDOWN_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const IDIQ_BPA_TOOLTIP_TEXT =
  'Please indicate whether this is an IDIQ or BPA Contract, yes or no. Note ' +
  'that if this is a IDIQ or BPA Contract, all existing Roster Owners and users ' +
  'will be removed from this contract, which may cause them to be off-boarded. ' +
  'If this is not an IDIQ or BPA Contract, you will be required to assign a Roster Owner.';

const CMS_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@cms\.hhs\.gov$/i;

type ResolvedDelegateCandidate = NonNullable<DelegateFederalApproverResolveResponse['data']>;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function ContractSettingsTab() {
  const baseTestId = 'contract-settings-tab';

  const { contractId, contract } = useContractData();
  const { successAlert, errorAlert } = useAlerts();
  const { getRolesForContract, hasRoleOnContract, isAdmin } = useRBAC();
  const updateContract = useUpdateContract();
  const contractRoles = getRolesForContract(contractId ?? '');
  const canManageAlternate =
    isAdmin || contractRoles.some((role) => role.role_type === CONTRACT_ROLE.COR && role.role_variant === 'PRIMARY');
  const isAlternateCor = contractRoles.some(
    (role) => role.role_type === CONTRACT_ROLE.COR && role.role_variant === 'ALTERNATE',
  );
  const canViewAlternate = canManageAlternate || isAlternateCor;
  const deleteRoleAssignment = useDeleteContractRoleAssignment(contractId ?? '');
  const resolveDelegate = useResolveDelegateFederalApprover(contractId ?? '');
  const upsertDelegate = useUpsertDelegateFederalApprover(contractId ?? '');
  const roleAssignments = useContractRoleAssignmentsQuery(contractId ?? '', {
    enabled: !!contractId && canViewAlternate,
    filters: { active: true, roleType: 'COR', roleVariant: 'ALTERNATE' },
  });
  const canEditContractSettings = isAdmin || hasRoleOnContract(contractId ?? '', CONTRACT_ROLE.COR);
  const alternateAssignment = roleAssignments.data?.data?.[0];

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [isDelegateWarningOpen, setIsDelegateWarningOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isIdiqBpa, setIsIdiqBpa] = useState(contract?.isIdiqBpa ? 'yes' : 'no');
  const [hasTemporarySeasonalUsers, setHasTemporarySeasonalUsers] = useState(
    contract?.hasTemporarySeasonalUsers ? 'yes' : 'no',
  );
  const [alternateEmailOverride, setAlternateEmailOverride] = useState<string | null>(null);
  const [alternateNameOverride, setAlternateNameOverride] = useState<string | null>(null);
  const [alternateEmailError, setAlternateEmailError] = useState('');

  // ACP-2092 (Section 508): Save rejecting the Delegate Federal Approver email showed the
  // message on the field and said nothing — focus stayed on Save. Same treatment as the
  // Roster Owners tab: announce the field and move focus to it.
  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({ errorSignature: alternateEmailError ? 'alternateCorEmail' : '' });
  const [resolvedDelegate, setResolvedDelegate] = useState<ResolvedDelegateCandidate | null>(null);

  if (!contract) {
    return (
      <div
        className='contract-settings-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-empty`}>
          No contract information available.
        </p>
      </div>
    );
  }

  const initialIsIdiqBpa = contract.isIdiqBpa ? 'yes' : 'no';
  const initialHasTemporarySeasonalUsers = contract.hasTemporarySeasonalUsers ? 'yes' : 'no';
  const initialAlternateEmail = alternateAssignment?.user?.email ?? '';
  const initialAlternateName = [alternateAssignment?.user?.firstName, alternateAssignment?.user?.lastName]
    .filter(Boolean)
    .join(' ');
  const alternateEmail = alternateEmailOverride ?? initialAlternateEmail;
  const alternateName = alternateNameOverride ?? initialAlternateName;
  const delegateEmailChanged = normalizeEmail(alternateEmail) !== normalizeEmail(initialAlternateEmail);
  const isDirty =
    isIdiqBpa !== initialIsIdiqBpa ||
    hasTemporarySeasonalUsers !== initialHasTemporarySeasonalUsers ||
    (canManageAlternate && delegateEmailChanged);

  const handleConfirmDiscard = () => {
    setIsIdiqBpa(initialIsIdiqBpa);
    setHasTemporarySeasonalUsers(initialHasTemporarySeasonalUsers);
    setAlternateEmailOverride(null);
    setAlternateNameOverride(null);
    setAlternateEmailError('');
    setResolvedDelegate(null);
    setSuccessMessage('Changes discarded.');
  };

  const handleAlternateEmailChange = (value: string) => {
    setAlternateEmailOverride(value);
    setAlternateEmailError('');
    setResolvedDelegate(null);
    setAlternateNameOverride((currentName) => {
      if (normalizeEmail(value) === normalizeEmail(initialAlternateEmail)) return initialAlternateName;
      if (!value.trim()) return '';

      return currentName === null || currentName === initialAlternateName ? '' : currentName;
    });
  };

  const handleSaveClick = async () => {
    setSuccessMessage('');
    setAlternateEmailError('');

    if (!canManageAlternate || !delegateEmailChanged || !alternateEmail.trim()) {
      setIsSaveConfirmOpen(true);

      return;
    }

    const normalizedEmail = normalizeEmail(alternateEmail);
    if (!CMS_EMAIL_PATTERN.test(normalizedEmail)) {
      setAlternateEmailError('Invalid email address');
      reportInvalidFields();

      return;
    }

    try {
      const response = await resolveDelegate.mutateAsync({ email: normalizedEmail });
      const candidate = response.data;
      if (!candidate) throw new Error('User not found in the AccessCMS system.');

      setResolvedDelegate(candidate);
      setAlternateEmailOverride(candidate.email);
      setAlternateNameOverride([candidate.firstName, candidate.lastName].filter(Boolean).join(' '));

      if (candidate.hasActiveCorAssignment) {
        setIsSaveConfirmOpen(true);
      } else {
        setIsDelegateWarningOpen(true);
      }
    } catch (error) {
      setAlternateNameOverride('');
      setAlternateEmailError(error instanceof Error ? error.message : 'User not found in the AccessCMS system.');
      reportInvalidFields();
    }
  };

  const handleConfirmSave = async (acknowledgeNoActiveCorAssignment = false) => {
    if (!contractId) return;

    try {
      if (canManageAlternate && delegateEmailChanged) {
        if (alternateEmail.trim()) {
          await upsertDelegate.mutateAsync({
            body: {
              acknowledgeNoActiveCorAssignment,
              email: normalizeEmail(alternateEmail),
            },
          });
        } else if (alternateAssignment?.id) {
          await deleteRoleAssignment.mutateAsync({
            assignmentId: alternateAssignment.id,
            contractId,
          });
        }
      }

      updateContract.mutate(
        {
          body: {
            hasTemporarySeasonalUsers: hasTemporarySeasonalUsers === 'yes',
            isIdiqBpa: isIdiqBpa === 'yes',
          },
          pathParams: { id: contractId },
        },
        {
          onError: () => {
            errorAlert({
              message: 'Please try again. If the problem persists, contact support.',
              title: 'Failed to save contract settings',
            });
          },
          onSuccess: () => {
            successAlert({
              dismissInSeconds: 30,
              title:
                canManageAlternate && delegateEmailChanged
                  ? 'Delegate Federal Approver updated successfully'
                  : 'Contract settings updated successfully',
            });
            setSuccessMessage('Contract settings saved successfully.');
          },
        },
      );
    } catch (error) {
      errorAlert({
        message: error instanceof Error ? error.message : 'Please try again. If the problem persists, contact support.',
        title: 'Failed to save contract settings',
      });
    }
  };

  return (
    <>
      <div
        ref={scopeRef}
        className='contract-settings-tab-content'
        data-testid={baseTestId}>
        <ValidationAnnouncement
          data-testid={`${baseTestId}-error-announcement`}
          message={errorAnnouncement}
        />
        <div
          className='contract-settings-content-container'
          data-testid={`${baseTestId}-content`}>
          <div>
            <p
              className='ds-u-margin-bottom--0'
              data-testid={`${baseTestId}-intro`}>
              If any of the information in this section is incorrect, please work with your Contracting Officer to have
              CALM updated.
            </p>
            <RequiredFieldsLegend
              className='ds-u-margin-top--0 ds-u-margin-bottom--2'
              data-testid={`${baseTestId}-required-fields`}
            />
            <h3
              className='ds-u-margin-top--0 ds-u-margin-bottom--1'
              data-testid={`${baseTestId}-settings-heading`}>
              Contract Settings{' '}
              <span
                aria-label='required'
                className='ds-u-color--error'>
                *
              </span>
            </h3>
            <p
              className='ds-u-margin-bottom--2'
              data-testid={`${baseTestId}-settings-hint`}>
              You must answer the following questions.
            </p>
          </div>

          <Dropdown
            required
            data-testid={`${baseTestId}-idiq-bpa`}
            disabled={!canEditContractSettings}
            label='Is this an IDIQ or BPA Contract?'
            name='isIdiqBpa'
            options={DROPDOWN_OPTIONS}
            tooltipText={IDIQ_BPA_TOOLTIP_TEXT}
            value={isIdiqBpa}
            onChange={setIsIdiqBpa}
          />

          <Dropdown
            required
            data-testid={`${baseTestId}-temp-seasonal`}
            disabled={!canEditContractSettings}
            label='Does this Contract have temporary or seasonal users?'
            name='hasTemporarySeasonalUsers'
            options={DROPDOWN_OPTIONS}
            tooltipText='Will any of the users on this contract be working for 6 months or less?'
            value={hasTemporarySeasonalUsers}
            onChange={setHasTemporarySeasonalUsers}
          />

          {canViewAlternate && (
            <>
              <TextField
                data-testid={`${baseTestId}-alternate-name`}
                label='Delegate Federal Approver Name'
                name='alternateCorName'
                readOnly={!canManageAlternate}
                tooltipText='Enter the name of the Delegate Federal Approver. After the email is verified, this is replaced with the canonical name from AccessCMS.'
                value={alternateName}
                onChange={(event) => setAlternateNameOverride(event.target.value)}
              />

              <TextField
                data-testid={`${baseTestId}-alternate-email`}
                errorMessage={alternateEmailError}
                label='Delegate Federal Approver Email'
                name='alternateCorEmail'
                readOnly={!canManageAlternate}
                tooltipText='Enter the CMS email address of the person who will act as the Delegate Federal Approver. Clear this field to remove the current assignment.'
                type='email'
                value={alternateEmail}
                onChange={(event) => handleAlternateEmailChange(event.target.value)}
              />
            </>
          )}

          <HelpDrawerLink
            className='ds-u-margin-top--2'
            data-testid={`${baseTestId}-help-link`}
            onClick={() => setIsHelpOpen(true)}
          />

          <Drawer
            data-testid={`${baseTestId}-drawer`}
            heading='Help: Contract Settings'
            isOpen={isHelpOpen}
            width='50%'
            onClose={() => setIsHelpOpen(false)}>
            <h3>Is this an IDIQ or BPA Contract?</h3>
            <p>
              IDIQ (Indefinite Delivery Indefinite Quantity) or BPA (Blanket Purchase Agreement) Contracts do not have
              users on them. If this is later changed to an IDIQ or BPA Contract, all existing Roster Owners and users
              will be removed from this contract. This may result in their offboarding if they are not on secondary
              contracts as well. If this is not an IDIQ or BPA Contract, a Roster Owner must be assigned.
            </p>
            <h3>Does this Contract have temporary or seasonal users?</h3>
            <p>
              Temporary or seasonal users are those who will work for six months or less. Not every Contract will have
              these users.
            </p>
            <h3>Delegate Federal Approver Name</h3>
            <p>
              The legal first and last name of someone who will act as the Delegate Federal Approver for this Contract
              while you are out of office.
            </p>
            <h3>Delegate Federal Approver Email</h3>
            <p>
              The email address of someone who will act as the Delegate Federal Approver for this Contract while you are
              out of office. A CMS email address must be used.
            </p>
          </Drawer>

          <ContractFormActions
            back={{
              'data-testid': `${baseTestId}-back`,
            }}
            data-testid={`${baseTestId}-actions`}
            discard={{
              'data-testid': `${baseTestId}-discard`,
              disabled: !isDirty,
              onClick: () => {
                setSuccessMessage('');
                setIsDiscardConfirmOpen(true);
              },
            }}
            isEditable={canEditContractSettings}
            save={{
              'data-testid': `${baseTestId}-save`,
              disabled:
                !isDirty ||
                updateContract.isPending ||
                deleteRoleAssignment.isPending ||
                resolveDelegate.isPending ||
                upsertDelegate.isPending,
              onClick: () => void handleSaveClick(),
            }}
          />
        </div>
      </div>
      <ConfirmActionModal
        cancelLabel='Cancel'
        confirmLabel='Save'
        data-testid={`${baseTestId}-save-modal`}
        description='Are you sure you want to save all your changes? This action cannot be undone and the contract will be saved with the current information.'
        isConfirming={deleteRoleAssignment.isPending || upsertDelegate.isPending}
        isOpen={isSaveConfirmOpen}
        successMessage={successMessage}
        title='Save Changes?'
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={() => handleConfirmSave(false)}
      />
      <ConfirmActionModal
        cancelLabel='Cancel'
        confirmLabel='Continue'
        data-testid={`${baseTestId}-delegate-warning-modal`}
        description={`${
          [resolvedDelegate?.firstName, resolvedDelegate?.lastName].filter(Boolean).join(' ') || 'This user'
        } is not an active Federal Approver (COR) on any contract. If you continue, this person will get Federal Approver (COR) permissions for the contract while you are out of the office. Do you want to continue?`}
        isConfirming={upsertDelegate.isPending}
        isOpen={isDelegateWarningOpen}
        title='Confirm Delegate Federal Approver'
        onClose={() => setIsDelegateWarningOpen(false)}
        onConfirm={() => handleConfirmSave(true)}
      />
      <ConfirmActionModal
        cancelLabel='Cancel'
        confirmLabel='Confirm Discard'
        data-testid={`${baseTestId}-discard-modal`}
        description='Are you sure you want to discard all your changes? This action cannot be undone and the contract will revert to the last time this was saved.'
        isOpen={isDiscardConfirmOpen}
        successMessage={successMessage}
        title='Discard Changes?'
        onClose={() => setIsDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
}

export default ContractSettingsTab;
